import "./DataCollection.css";
import InputRowGroup from "./Inputs/InputRowGroup";
import InputRow from "./Inputs/InputRow";
import TextInput from "./Inputs/TextInput";
import DateInput from "./Inputs/DateInput";
import BooleanInput from "./Inputs/BooleanInput";
import { beneficiaryAge } from "../Utils/PlanUtils";
import { handleChange } from "../Utils/DataCollectionUtils";
import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import DropdownInput from "./Inputs/DropdownInput";
import { useAuthenticationStatus } from "../Authentication/AuthenticationStatusProvider";
import { shouldDisplayProvincialGrantOption } from "../Utils/PlanUtils";
import { sprintf } from "../Utils/FormattingUtils";
import { useParams, Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";

const BasicInputDataCollection = ({ state }) => {
  const params = useParams();
  const { t } = useTranslation();
  const { authenticationStatus } = useAuthenticationStatus();
  const [agentOptions, setAgentOptions] = useState([]);
  const [provinceError, setProvinceError] = useState("");

  //When the user changes, we load the agent options
  useEffect(() => {
    if (!authenticationStatus.user) {
      return;
    }
    setAgentOptions(
      authenticationStatus.user.agentCodes.map((code) => {
        return {
          label: `${code.firstName} ${code.lastName} - ${code.code} - ${t(code.province)}`,
          value: code.code,
        };
      }),
    );

    if (
      state.agent.code &&
      authenticationStatus.user.agentCodes.find(
        (code) => code.code === state.agent.code,
      ) === undefined
    ) {
      setAgentOptions([
        ...authenticationStatus.user.agentCodes.map((code) => {
          return {
            label: `${code.firstName} ${code.lastName} - ${code.code} - ${t(code.province)}`,
            value: code.code,
          };
        }),
        {
          label: `unknown - ${state.agent.code} - unknown`,
          value: state.agent.code,
        },
      ]);
    }
  }, [authenticationStatus.user]);

  //When the number of agent options changes (i.e. we just loaded the user) we can set the default agent and signing province
  useEffect(() => {
    if (agentOptions.length > 0) {
      if (!state.common.signingProvince) {
        const signingProvince = authenticationStatus.user.agentCodes.find(
          (code) => code.code === agentOptions[0].value,
        ).province;
        state.setCommon({ ...state.common, signingProvince: signingProvince });
      }
      if (!state.agent.code) {
        state.setAgent({ ...state.agent, code: agentOptions[0].value });
      }
    }
  }, [agentOptions]);

  //Friendly string indicating how old the beneficiary currently is
  const beneficiaryAgeString = () => {
    if (!state.beneficiary.birthdate) {
      return "";
    }
    let name = t("The beneficiary");
    if (state.beneficiary.firstName && state.beneficiary.lastName) {
      name = `${state.beneficiary.firstName} ${state.beneficiary.lastName}`;
    }
    const age = beneficiaryAge(state.beneficiary.birthdate);
    if (age < 1) {
      return sprintf(t("Beni_under_1"), name);
    } else if (age == 1) {
      return sprintf(t("Beni_is_1"), name);
    } else {
      return sprintf(t("Beni_over_1"), name, age);
    }
  };

  return (
    <form>
      {state.application && state.application.masterApplicationId && (
        <InputRowGroup label={t("Family Enrolment")}>
          <InputRow>
            <div className={"info"}>
              {t(
                "This enrolment is a secondary enrolment of a Family Enrolment. Know Your Client and Subscriber information must be completed on the primary enrolment.",
              )}
            </div>
          </InputRow>
        </InputRowGroup>
      )}
      {state.application &&
        state.application.childApplicationIds &&
        state.application.childApplicationIds.length > 0 && (
          <InputRowGroup label={t("Family Enrolment")}>
            <InputRow>
              <div className={"info"}>
                {t(
                  "This enrolment is the primary enrolment of a Family Enrolment. Know Your Client and Subscriber details entered on this enrolment here will apply to all secondary enrolments.",
                )}
              </div>
            </InputRow>
          </InputRowGroup>
        )}
      {state.application && !state.application.masterApplicationId && (
        <InputRowGroup label={t("Family Information")}>
          <InputRow label={t("Subscription Type")}>
            <DropdownInput
              name={"subscriber2Include"}
              state={state.common}
              onChange={handleChange(state.setCommon, true)}
              options={[
                { label: "Single", value: false },
                { label: "Joint", value: true },
              ]}
            />
          </InputRow>

          <InputRow label={t("Subscriber")}>
            <TextInput
              placeholder={t("Given Name")}
              name={"firstName"}
              state={state.subscriber}
              onChange={handleChange(state.setSubscriber)}
              maxLength={50}
            />
            <TextInput
              placeholder={t("Last Name")}
              name={t("lastName")}
              state={state.subscriber}
              onChange={handleChange(state.setSubscriber)}
              maxLength={50}
            />
          </InputRow>

          {state.common.subscriber2Include && (
            <InputRow label={t("Joint Subscriber")}>
              <TextInput
                placeholder={t("Given Name")}
                name={"firstName"}
                state={state.jointSubscriber}
                onChange={handleChange(state.setJointSubscriber)}
                maxLength={50}
              />
              <TextInput
                placeholder={t("Last Name")}
                name={"lastName"}
                state={state.jointSubscriber}
                onChange={handleChange(state.setJointSubscriber)}
                maxLength={50}
              />
            </InputRow>
          )}

          {authenticationStatus.user && (
            <InputRow label={t("Dealing Representative")}>
              <DropdownInput
                name={"code"}
                state={state.agent}
                onChange={handleChange(state.setAgent)}
                options={agentOptions}
              />
            </InputRow>
          )}

          {state.subscriber.province !== state.agent.province && (
            <InputRow>
              <div className={"centered-text financial-warning"}>
                {sprintf(
                  t(`Your DR code %@ is not licensed in the province %@.`),
                  state.agent.code,
                  state.subscriber.province,
                )}
              </div>
            </InputRow>
          )}
        </InputRowGroup>
      )}

      <InputRowGroup label={t("Beneficiary Information")}>
        <InputRow label={t("Beneficiary")}>
          <TextInput
            placeholder={t("Given Name")}
            name={"firstName"}
            state={state.beneficiary}
            onChange={handleChange(state.setBeneficiary)}
            maxLength={50}
          />
          <TextInput
            placeholder={t("Last Name")}
            name={"lastName"}
            state={state.beneficiary}
            onChange={handleChange(state.setBeneficiary)}
            maxLength={50}
          />
        </InputRow>

        <InputRow label={t("Beneficiary Birth Date")}>
          <DateInput
            name={"birthdate"}
            state={state.beneficiary}
            onChange={handleChange(state.setBeneficiary)}
            maxDate={new Date().toISOString().split("T")[0]}
          />
        </InputRow>
        {state.beneficiary.birthdate && (
          <InputRow>
            <div className={"centered-text"}>{beneficiaryAgeString()}</div>
          </InputRow>
        )}
        {!state.beneficiary.birthdate && (
          <InputRow>
            <div className={"financial-warning"}>
              {t("Beneficiary birth date is a required field.")}
            </div>
          </InputRow>
        )}
      </InputRowGroup>

      <InputRowGroup label={t("Federal Grants")}>
        <InputRow label={t("Canada Education Savings Grants")}>
          <BooleanInput
            label={t("Yes")}
            name={"grantCanadaEducationSavings"}
            state={state.application}
            onChange={handleChange(state.setApplication)}
          />
        </InputRow>
        <InputRow label={t("Canada Learning Bond")}>
          <BooleanInput
            label={t("Yes")}
            name={"grantCanadaLearningBond"}
            state={state.application}
            onChange={handleChange(state.setApplication)}
          />
        </InputRow>
      </InputRowGroup>

      {shouldDisplayProvincialGrantOption(state) && (
        <InputRowGroup label={t("Provincial Grants")}>
          <InputRow label={t("Provincial Grant")}>
            <BooleanInput
              name={"grantProvincial"}
              n
              state={state.application}
              onChange={handleChange(state.setApplication)}
            />
          </InputRow>
        </InputRowGroup>
      )}

      <InputRowGroup label={t("Trusted Contact Person")}>
        <InputRow label={t("Trusted Contact Person")}>
          <TextInput
            placeholder={t("Full Name")}
            name={"tcpName"}
            state={state.application}
            onChange={handleChange(state.setApplication)}
            maxLength={70}
          />
        </InputRow>

        <InputRow label={t("Relationship")}>
          <TextInput
            placeholder={t("Relationship to Subscriber")}
            name={"tcpRelationship"}
            state={state.application}
            onChange={handleChange(state.setApplication)}
            maxLength={30}
          />
        </InputRow>

        <InputRow label={t("Phone Number")}>
          <TextInput
            placeholder={t("Phone Number")}
            name={"tcpPhone"}
            state={state.application}
            onChange={handleChange(state.setApplication)}
            maxLength={20}
            validationRules={{
              pattern: {
                value: /^[\d-]*$/,
              },
            }}
            validationFailedMessage={"The phone number entered was invalid"}
          />
        </InputRow>

        <InputRow label={t("E-Mail Address")}>
          <TextInput
            placeholder={t("E-Mail Address")}
            name={"tcpEmail"}
            state={state.application}
            onChange={handleChange(state.setApplication)}
            maxLength={50}
            validationRules={{
              pattern: {
                value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
              },
            }}
            validationFailedMessage={"The E-Mail address entered was invalid"}
          />
        </InputRow>
      </InputRowGroup>
      <Tooltip
        id={"tooltip"}
        className={"enrollment-progress-stepper-tooltip"}
      />
    </form>
  );
};

export default BasicInputDataCollection;
